import React, { useState } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
// Paleta institucional Elecom — tokens compartidos (ver src/theme/colors.js)
import { COLORS as C } from '../theme/colors';

/**
 * @description Shell principal de la aplicación.
 * Incluye header con logo, navegación horizontal (desktop) y drawer (mobile).
 */
const Layout = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div style={styles.shell}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={styles.header} className="elecom-header">
        {/* Logo */}
        <img
          src={process.env.PUBLIC_URL + "/logo.png"}
          alt="Elecom Elevator Components"
          style={styles.logo}
        />

        {/* Botón hamburguesa — solo visible en mobile via CSS */}
        <button
          className="hamburger-btn"
          onClick={() => setDrawerOpen(true)}
          aria-label="Abrir menú"
        >
          ☰
        </button>

        {/* Acciones de usuario */}
        {currentUser && (
          <div style={styles.userArea}>
            <span style={styles.userName} className="user-name">
              {currentUser.username ?? currentUser.email}
            </span>
            <span style={styles.userRole} className="user-role">
              {currentUser.role}
            </span>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              Cerrar sesión
            </button>
          </div>
        )}
      </header>

      {/* ── Navegación desktop ─────────────────────────────────────────── */}
      <nav style={styles.nav} className="top-nav">
        <NavLinks currentUser={currentUser} styleFunc={navStyle} onClick={null} />
      </nav>

      {/* ── Overlay del drawer ─────────────────────────────────────────── */}
      <div
        className={`drawer-overlay${drawerOpen ? ' visible' : ''}`}
        onClick={closeDrawer}
      />

      {/* ── Drawer mobile ──────────────────────────────────────────────── */}
      <nav className={`drawer-nav${drawerOpen ? ' drawer-open' : ''}`} aria-label="Navegación principal">
        <button
          onClick={closeDrawer}
          style={styles.drawerClose}
          aria-label="Cerrar menú"
        >
          &times;
        </button>
        <NavLinks currentUser={currentUser} styleFunc={drawerNavStyle} onClick={closeDrawer} />
      </nav>

      {/* ── Contenido ──────────────────────────────────────────────────── */}
      <main style={styles.main} className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

/**
 * @description Renderiza los links de navegación.
 * Reutilizado tanto en el nav desktop como en el drawer mobile.
 * @param {object} props
 * @param {object|null} props.currentUser Usuario autenticado actual.
 * @param {Function} props.styleFunc Función de estilo para NavLink (navStyle o drawerNavStyle).
 * @param {Function|null} props.onClick Callback al hacer clic en un link (cierra el drawer en mobile).
 */
const NavLinks = ({ currentUser, styleFunc, onClick }) => (
  <>
    <NavLink to="/dashboard" style={styleFunc} end onClick={onClick}>
      Dashboard
    </NavLink>
    <NavLink to="/projects" style={styleFunc} onClick={onClick}>
      Proyectos
    </NavLink>
    <NavLink to="/work-orders" style={styleFunc} onClick={onClick}>
      Órdenes de Trabajo
    </NavLink>
    <NavLink to="/equipment" style={styleFunc} onClick={onClick}>
      Equipos
    </NavLink>
    <NavLink to="/maintenance-reports" style={styleFunc} onClick={onClick}>
      Reportes de Mantenimiento
    </NavLink>
    <NavLink to="/maintenance-orders" style={styleFunc} onClick={onClick}>
      Órdenes de Mantenimiento
    </NavLink>
    {currentUser?.role !== 'TECHNICIAN' && (
      <NavLink to="/users" style={styleFunc} onClick={onClick}>
        Usuarios
      </NavLink>
    )}
  </>
);

/**
 * @description Estilo para NavLink en la barra de navegación desktop.
 * Aplica teal y subrayado cuando el link está activo.
 */
const navStyle = ({ isActive }) => ({
  ...styles.navLink,
  color: isActive ? C.teal : C.charcoal,
  borderBottom: isActive ? `2px solid ${C.teal}` : '2px solid transparent',
  fontWeight: isActive ? '600' : '400',
});

/**
 * @description Estilo para NavLink en el drawer mobile.
 * Diseño vertical con borde izquierdo de color activo.
 */
const drawerNavStyle = ({ isActive }) => ({
  display: 'block',
  padding: '14px 24px',
  fontSize: '15px',
  textDecoration: 'none',
  color: isActive ? C.teal : C.charcoal,
  borderLeft: isActive ? `3px solid ${C.teal}` : `3px solid transparent`,
  fontWeight: isActive ? '600' : '400',
  backgroundColor: isActive ? 'rgba(26,184,171,0.06)' : 'transparent',
});

const styles = {
  shell: {
    minHeight: '100vh',
    backgroundColor: C.pageBg,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 32px',
    height: '64px',
    backgroundColor: C.white,
    borderBottom: `1px solid rgba(56,62,65,0.1)`,
    boxShadow: '0 1px 4px rgba(56,62,65,0.08)',
    position: 'relative',
    zIndex: 100,
  },
  logo: {
    height: '38px',
    objectFit: 'contain',
  },
  userArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '600',
    color: C.charcoal,
  },
  userRole: {
    fontSize: '11px',
    fontWeight: '600',
    color: C.darkTeal,
    backgroundColor: `rgba(26,184,171,0.1)`,
    padding: '2px 8px',
    borderRadius: '10px',
    letterSpacing: '0.4px',
  },
  logoutBtn: {
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: '500',
    color: C.charcoal,
    background: 'none',
    border: `1px solid rgba(56,62,65,0.25)`,
    borderRadius: '6px',
    cursor: 'pointer',
  },
  nav: {
    display: 'flex',
    gap: '4px',
    padding: '0 32px',
    backgroundColor: C.white,
    borderBottom: `1px solid rgba(56,62,65,0.08)`,
  },
  navLink: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '14px 14px 12px',
    fontSize: '14px',
    textDecoration: 'none',
    transition: 'color 0.15s',
  },
  main: {
    padding: '28px 32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  drawerClose: {
    alignSelf: 'flex-end',
    marginRight: '16px',
    marginBottom: '8px',
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: C.charcoal,
    padding: '0',
    lineHeight: '1',
  },
};

export default Layout;
